import config from 'config';

import {
  cioFacetsToUrlFilters,
  cioSizeFacetsToFilterPreferences,
  MAX_FACET_COUNT,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import {
  sizeByKey,
  EXPANDED_PLUS_SIZES,
  REDACTED_PLUS_SIZES,
  POST_COMBINED_PLUS_SIZES,
} from '~/shared/utils/sizeConstants';
import { parseBraSizes } from '~/techstyle-shared/react-components/src/utils/sizes';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';

import { GRID_TYPES } from '../hooks/useGridInputs';

import { getNewQueryParamString, QueryParamBehavior } from './routing';

/**
 * Checks if the given facet name is a size facet.
 * Specific to Constructor data shape.
 *
 * @param {string} facetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a size facet.
 */
export const isCioSizeFacet = (facetName) => {
  return facetName?.startsWith('available_size_');
};

/**
 * Checks if the given facet name corresponds to a size facet, regardless of API.
 * Constructor uses `available_size_*`, ProductsV2 uses `size_*`. We want both
 * to map to the same canonical RB key (see `getFacetRBKey`).
 *
 * Matches only the canonical prefixes — names that merely contain "size" as a
 * substring (e.g. `oversized_collection`, `customizable_sizing_tier`) are NOT
 * size facets and must not be namespaced under the size RB key.
 *
 * @param {string} facetName - The facet name to check.
 * @return {boolean} - True if the facet name is a size facet.
 */
export const isSizeFacet = (facetName) => {
  return Boolean(isCioSizeFacet(facetName) || isProductsV2SizeFacet(facetName));
};

/**
 * Returns the canonical resource bundle key for a facet group label
 * (the parent filter, e.g. "Color", "Bottom Size").
 * - Size facets normalize to `available_<facetName>` so a single CSV entry
 *   covers both Constructor (`available_size_*`) and ProductsV2 (`size_*`).
 * - Non-size facets become `tag_<facetName>_list`. Idempotent if the prefix
 *   or suffix is already present.
 *
 * @param {string} facetName - The facet name from the API response.
 * @return {string} - The RB key (without group prefix).
 */
export const getFacetRBKey = (facetName) => {
  if (!facetName) {
    return facetName;
  }

  if (isSizeFacet(facetName)) {
    return facetName.startsWith('available_')
      ? facetName
      : `available_${facetName}`;
  }

  let result = facetName;
  if (!result.startsWith('tag_')) {
    result = `tag_${result}`;
  }
  if (!result.endsWith('_list')) {
    result = `${result}_list`;
  }
  return result;
};

/**
 * Returns the canonical resource bundle key for a facet option value
 * (the child filter, e.g. "Black", "Small").
 * - Size options namespace under their facet key: `<facetRBKey>:<slug>`.
 * - Non-size options use a flat slug. Collisions across facets are a content
 *   concern — the CSV is the source of truth.
 *
 * @param {string} facetName - The facet name the option belongs to.
 * @param {string} facetValue - The option value.
 * @return {string} - The RB key (without group prefix).
 */
export const getFacetOptionRBKey = (facetName, facetValue) => {
  if (!facetValue) {
    return '';
  }
  const slug = slugify(facetValue);
  if (isSizeFacet(facetName)) {
    return `${getFacetRBKey(facetName)}:${slug}`;
  }
  return slug;
};

/**
 * Checks if the given facet name is a ProductsV2 size facet.
 * Specific to ProductsV2 data shape.
 *
 * @param {string} facetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a Products V2 size facet.
 */
export const isProductsV2SizeFacet = (facetName) => {
  return facetName?.startsWith('size_');
};

/**
 * Checks if the given facet name is a color facet.
 * Specific to Constructor data shape.
 *
 * @param {string} facetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a color facet.
 */
export const isCioColorFacet = (facetName) => {
  const colorFacets = ['tag_color_list', 'tag_color_label'];
  return colorFacets.includes(facetName);
};

/** *
 * Checks if the given facet name is a ProductsV2 color facet.
 * Specific to ProductsV2 data shape.
 *
 * @param {string} facetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a Products V2 facet.
 */
export const isProductsV2ColorFacet = (facetName) => {
  return facetName === 'color';
};

/**
 * Checks if the given facet name is a custom bra filter.
 * This is specific to the Savage brand.
 *
 * @param {string} facetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a custom bra filter.
 */
export const isCustomBraFilter = (facetName) => {
  const customBraFilter = config.get('public.brand.features.customBraFilter');
  if (!customBraFilter) {
    return false;
  }

  return facetName === 'available_size_bra' || facetName === 'size_bra';
};

/**
 * Updates the selected filters in the URL params, which we use as the source of truth for state management.
 * All data fetching is based on the URL params.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.facetName - The name of the facet to update.
 * @param {string} params.facetOption - The option of the facet to update.
 * @param {Object} params.router - The router object for navigation.
 * @param {QueryParamBehavior} params.queryParamBehavior - The behavior for updating the query params. Default is TOGGLE.
 * @returns {void}
 */
export const updateFiltersInUrlParams = ({
  facetName,
  facetOption,
  router,
  queryParamBehavior = QueryParamBehavior.TOGGLE,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const newQueryParamString = getNewQueryParamString({
    query,
    newQueryParams: { [facetName]: facetOption },
    queryParamBehavior,
  });

  if (!newQueryParamString) {
    router.replace(router.pathname, aspathname, {
      shallow: true,
    });
    return;
  }

  router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Clears all CIO facets from the URL params, preserving other query params.
 * This is used when the user clears all filters.
 *
 * @param {Object} router - The router object for navigation.
 * @param {Array} allRegisteredFacetNames - An array of all registered facet names to be cleared.
 * @param {string} gridType - The type of the grid (e.g., CONSTRUCTOR_SEARCH_GRID).
 * @returns {void}
 */
export const clearAllFacetsFromUrlParams = ({
  router,
  allRegisteredFacetNames,
  gridType,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const urlParams = new URLSearchParams(query);

  // Only remove facets from query params
  // Preserve other query params like tabId and utm params
  const preservedQueryParams = {};
  urlParams.forEach((value, key) => {
    const mappedKey =
      gridType === GRID_TYPES.PRODUCTS_V2_GRID
        ? key
        : urlFiltersToCioFacets[key] || key;

    if (!allRegisteredFacetNames.includes(mappedKey)) {
      preservedQueryParams[mappedKey] = value;
    }
  });

  const newQueryParamString = new URLSearchParams(
    preservedQueryParams
  ).toString();

  if (!newQueryParamString) {
    router.replace(router.pathname, aspathname, {
      shallow: true,
    });
    return;
  }

  router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Returns the display name of the selected facet option.
 * If the selected facet is a size facet, it appends the selected facet display name.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.selectedFacetName - The name of the selected facet.
 * @param {string} params.selectedFacetDisplayName - The display name of the selected facet.
 * @param {string} params.selectedFacetOptionDisplayName - The display name of the selected facet option.
 * @returns {string} - The transformed display name of the selected facet option.
 *
 * @example
 * facetName          | displayName
 * -------------------|-----------------
 * available_size_top | S | 6 (Top Size)
 * tag_color_list     | Red
 */
export const getTransformedFacetOptionDisplayName = ({
  selectedFacetName,
  selectedFacetDisplayName,
  selectedFacetOptionDisplayName,
}) => {
  if (isCioSizeFacet(selectedFacetName)) {
    return `${selectedFacetOptionDisplayName} (${selectedFacetDisplayName})`;
  }

  return selectedFacetOptionDisplayName;
};

/**
 * Transforms selected URL filters into a structured array of selected filter option objects.
 * Handles size translation, facet name mapping, and display name formatting.
 *
 * @param {object} params - The parameters object
 * @param {object} params.selectedUrlFilters - URL filter parameters (key-value pairs)
 * @param {Array} params.allRegisteredFacets - All available facets with their options
 * @param {string} params.gridType - The type of grid (e.g., GRID_TYPES.PRODUCTS_V2_GRID)
 * @param {string} params.domain - The current domain
 * @param {string} params.gender - The current gender context
 * @returns {Array} Array of selected filter option objects with facetName, urlName, facetOption, and facetOptionDisplayName
 */
export const getTransformedSelectedFilters = ({
  selectedUrlFilters,
  allRegisteredFacets,
  gridType,
  domain,
  gender,
}) => {
  // TODO: refactor to make it API agnostic (cio vs products/v2)
  // TODO: see if we can reduce the mapping and simply render the url filters directly

  return Object.entries(selectedUrlFilters).flatMap(([key, value]) => {
    const facetName = urlFiltersToCioFacets[key] || key;
    const facet = allRegisteredFacets?.find(
      (facet) =>
        facet?.name ===
        (gridType === GRID_TYPES.PRODUCTS_V2_GRID ? key : facetName)
    );

    if (!facet) {
      return [];
    }

    return value
      ?.map((option) => {
        const categoryName = cioSizeFacetsToFilterPreferences[facet.name];
        const translatedName = sizeByKey({
          domain,
          gender,
          category: categoryName,
          key: option,
        });
        const cioOption = facet?.options?.find(
          (facetOption) => facetOption?.value === option
        );

        if (!cioOption) {
          return null;
        }

        return {
          facetName,
          urlName: key,
          facetOption: translatedName || option,
          facetOptionDisplayName: getTransformedFacetOptionDisplayName({
            selectedFacetName: facetName,
            selectedFacetDisplayName: facet?.display_name,
            selectedFacetOptionDisplayName: cioOption?.display_name,
          }),
        };
      })
      ?.filter(Boolean);
  });
};

/**
 * Helper function for isolateSizeFacets, combines expanded plus sizes
 * into a single 'XXL-1X' size.
 * This is used to normalize the size filters for Fabletics.
 * @param {object} facet - The facet object to filter, guaranteed to be a size facet.
 * @returns {object} filtered facet with expanded plus sizes combined
 *
 * Note: This function is specific to Fabletics, not Savage, as Savage has different sizes.
 * It filters out 'XX-Large' and '1X' options to consolidate them into 'XXL-1X'.
 */
const filterPlusSizes = (facet) => {
  const plusSizeFilters = config.get('public.brand.features.plusSizeFilters');
  if (!plusSizeFilters) {
    return facet;
  }

  // When activeProducts calls isolateSizeFacets, facets has values array (array of strings).
  // order does not matter.
  if (facet.values) {
    const filteredFacets = facet.values.filter(
      (value) => !REDACTED_PLUS_SIZES.includes(value)
    );
    // If redacted plus sizes were removed, check if we should inject XXL-1X.
    if (filteredFacets.length !== facet.values.length) {
      if (!filteredFacets.includes('XXL-1X')) {
        filteredFacets.push('XXL-1X');
      }
    }
    facet.values = filteredFacets;
    return facet;
  }

  // When groupSizeFacets calls isolateSizeFacets, facets has options.
  // Options are an array of objects {count, display_name, value}.
  // Order matters.
  if (facet.options) {
    const nonCombinedSizes = [];
    const combinedPlusSizes = [];

    // NOTE: constructor adds 'XXL-1X' to beginning of options array if an injected
    // XXL-1X size is added (ie base only included 'XX-Large' and/or '1X', and we
    // removed those to manually add 'XXL-1X' size).
    // Cannot assume that combined plus sizes are in proper order.
    facet.options.forEach((option) => {
      if (EXPANDED_PLUS_SIZES.includes(option.value)) {
        combinedPlusSizes.push(option);
      } else {
        nonCombinedSizes.push(option);
      }
    });

    // no expanded plus sizes found, or only 'XXL-1X' found, return facet unchanged
    if (
      combinedPlusSizes.length === 0 ||
      (combinedPlusSizes.length === 1 &&
        combinedPlusSizes[0].value === 'XXL-1X')
    ) {
      return facet;
    }

    // otherwise we need to combine them into a single 'XXL-1X' size and inject it
    // into the appropriate place between the nonCombinedSizes

    const combinedPlusSize = {
      count: 0,
      display_name: 'XXL-1X | 16-18',
      value: 'XXL-1X',
      data: {},
      status: '',
    };
    combinedPlusSizes.forEach((option) => {
      combinedPlusSize.count += option.count || 0;
    });

    // split nonCombinedPlusSizes into prePlusSizes and postPlusSizes, where
    // postPlusSizes are sizes with option.value in ['2X', '3X', '4X', '5X']
    const preCombinedPlusSizes = [];
    const postCombinedPlusSizes = [];

    nonCombinedSizes.forEach((option) => {
      if (POST_COMBINED_PLUS_SIZES.includes(option.value)) {
        postCombinedPlusSizes.push(option);
      } else {
        preCombinedPlusSizes.push(option);
      }
    });

    return {
      ...facet,
      options: [
        ...preCombinedPlusSizes,
        combinedPlusSize,
        ...postCombinedPlusSizes,
      ],
    };
  }

  // dont think this should be reached?
  return facet;
};

export const isolateSizeFacets = (facets = []) => {
  const sizeFacets = [];
  const nonSizeFacets = [];

  facets.forEach((facet) => {
    if (isCioSizeFacet(facet.name) || isProductsV2SizeFacet(facet.name)) {
      const plusSizeFilteredFacet = filterPlusSizes(facet);
      sizeFacets.push(plusSizeFilteredFacet);
    } else {
      nonSizeFacets.push(facet);
    }
  });

  return { sizeFacets, nonSizeFacets };
};

/**
 * Group size facets together in the list of facets.
 * Size facets need to be grouped together for proper UI rendering and functionality.
 * All other facets are preserved in their original order.
 *
 * @param {Array} facets - The list of facets to be grouped.
 * @returns {Array} - The list of facets with size facets grouped together.
 */
export const groupSizeFacets = (facets = []) => {
  const firstSizeFacetIndex = facets.findIndex(
    (facet) => isCioSizeFacet(facet.name) || isProductsV2SizeFacet(facet.name)
  );

  const { sizeFacets, nonSizeFacets } = isolateSizeFacets(facets);

  if (!sizeFacets.length) {
    return facets;
  }

  const groupedSizeFacet = {
    display_name: 'Size', // needs to be localized via RB?
    name: 'size',
    sub_facets: sizeFacets,
  };

  return nonSizeFacets?.toSpliced(firstSizeFacetIndex, 0, groupedSizeFacet);
};

/**
 * We can't use the `selected` directly from facets from the CIO API response, due to size filters
 * being in cio prefilter expressions instead of the filters param. This results in the size filters
 * not having the selected value in the response. We also have to work with multiple engines, like
 * products/v2 grids, which do not have a `selected` property on facets. As a result, we have to
 * handle selected filters by checking the URL params as our source of truth.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.facetName - The name of the facet.
 * @param {string} params.facetOption - The value of the facet option.
 * @param {Object} params.selectedUrlFilters - The current URL filters.
 *
 * @returns {boolean} - Returns true if the facet option is selected, false otherwise.
 */
export const isFacetOptionSelected = ({
  facetName,
  facetOption,
  selectedUrlFilters,
}) => {
  if (!selectedUrlFilters || !facetName || !facetOption) {
    return false;
  }

  const urlFacetName = cioFacetsToUrlFilters[facetName] || facetName;
  return Boolean(
    selectedUrlFilters?.[urlFacetName]?.some(
      (option) => String(option) === String(facetOption)
    )
  );
};

/**
 * Counts the number of selected options in a facet.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.name - The name of the facet.
 * @param {Array} params.subFacets - The sub-facets of the facet. Does not exist for all facets. Mainly for grouped size facets.
 * @param {Array} params.options - The options of the facet.
 * @param {Object} params.selectedUrlFilters - The current URL filters.
 * @returns {number} - The count of selected options.
 */
export const getSelectedOptionCount = ({
  name,
  subFacets,
  options = [],
  selectedUrlFilters,
}) => {
  if (subFacets) {
    return subFacets.reduce((total, subFacet) => {
      return subFacet?.options?.reduce((subTotal, subFacetOption) => {
        return isFacetOptionSelected({
          facetName: subFacet?.name,
          facetOption: subFacetOption?.value,
          selectedUrlFilters,
        })
          ? subTotal + 1
          : subTotal;
      }, total);
    }, 0);
  }

  return options?.reduce((total, option) => {
    return isFacetOptionSelected({
      facetName: name,
      facetOption: option?.value,
      selectedUrlFilters,
    })
      ? total + 1
      : total;
  }, 0);
};

/**
 * Returns the all of the registered facets based on the selected URL filters.
 * (Some are only applied via auto-size filters and not via the UI selectors)
 * This is needed because the size filters are not included in the CIO API response,
 * due to them being in the prefilter expressions instead of the filters param.
 * We need to handle selected filters by checking the URL params as our source of truth.
 *
 * Note: Since we are using `allRegisteredFacets` to get the selected size facets, the `count` property
 * is not accurate for the current results of the grid. We are currently not using the `count` property
 * so this is not a problem, but be aware of this technical limitation when using size filters in prefilter expressions.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.selectedUrlFilters - The current URL filters.
 * @param {Array} params.allRegisteredFacets - The list of all registered facets.
 * @param {Array} params.facets - The list of api response facets (either from CIO or products/v2).
 * @param {string} params.gridType - The type of the grid (e.g., CONSTRUCTOR_SEARCH_GRID).
 * @returns {Array} - The list of selected size facets.
 */
export const getAllFacetsWithSelectedUrlFilters = ({
  selectedUrlFilters = {},
  allRegisteredFacets = [],
  facets = [],
  gridType,
}) => {
  const selectedSizeFacets = Object.entries(selectedUrlFilters).reduce(
    (acc, [key, value]) => {
      const facetName = urlFiltersToCioFacets[key] || key;
      const facet = allRegisteredFacets?.find(
        (facet) => facet?.name === facetName
      );

      // Ignore all non-size facets
      if (!isCioSizeFacet(facetName) || !facet) {
        return acc;
      }

      // Get the current options for the size facet from the facets (normal API response flow)
      const currentFacetOptions = facets?.find(
        (facet) => facet?.name === facetName
      )?.options;

      // Get the selected size options from the URL filters and map them based on the superset of all facets.
      const selectedSizeOptions = value
        ?.map((option) => {
          const cioOption = facet?.options?.find(
            (facetOption) => facetOption?.value === option
          );
          return cioOption;
        })
        ?.filter(Boolean);

      // De-dupe options that are already in the currentFacetOptions
      const selectedSizeOptionsNotInfacets = selectedSizeOptions?.filter(
        (option) =>
          !currentFacetOptions?.some(
            (currentOption) => currentOption?.value === option?.value
          )
      );

      // Append selected size options to the currentFacetOptions.
      // By default, selected size options not found in the currentFacetOptions
      // will appear at the top of the list.
      const combinedSelectedSizeOptions = [
        ...(selectedSizeOptionsNotInfacets || []),
        ...(currentFacetOptions || []),
      ];

      return [
        ...acc,
        {
          ...facet,
          options: combinedSelectedSizeOptions,
        },
      ];
    },
    []
  );

  const remainingFacets = facets?.filter(
    (facet) =>
      !selectedSizeFacets.some(
        (selectedFacet) => selectedFacet.name === facet.name
      )
  );

  const selectedRemainingFacets = remainingFacets?.filter(
    (facet) =>
      selectedUrlFilters?.[cioFacetsToUrlFilters[facet.name] || facet.name]
  );
  const nonSelectedRemainingFacets = remainingFacets?.filter(
    (facet) =>
      !selectedUrlFilters?.[cioFacetsToUrlFilters[facet.name] || facet.name]
  );

  // For Search Grid, we prioritize showing selected size facets, then selected non-size facets,
  // then non-selected remaining facets. This is to ensure we keep size facets at the top and render all facets with selected options first,
  // due to the search feature of truncating the list of facets to MAX_FACET_COUNT.
  // For other grid types, we just return the selected size facets and remaining facets in their
  // original order, as the search grid is the only one that truncates the list of facets.
  const orderedFacets =
    gridType === GRID_TYPES.CONSTRUCTOR_SEARCH_GRID
      ? [
          ...selectedSizeFacets,
          ...selectedRemainingFacets,
          ...nonSelectedRemainingFacets,
        ]
      : [...selectedSizeFacets, ...remainingFacets];
  return orderedFacets;
};

/**
 * Returns a mapped value from `cioFacetsToUrlFilters` if available
 * Then, removes 'tag_' prefix and '_list' suffix from the given string.
 * Converts result to use kebab case (slugified).
 * Per FLD-2548, Savage brand does process the prefix and suffix.
 * Pending product to align with syncing RBs and data autotags to use the same naming conventions
 * as all other brands.
 *
 * @param {string} filterName - The input string to sanitize.
 * @returns {string} The sanitized string in kebab case.
 */
export const sanitizeFilterName = (filterName) => {
  if (typeof filterName !== 'string') {
    return '';
  }

  // Ensure there was no previous slugification into kebab case for cio to url filters mapping
  const snakeCaseFacetName = filterName.replace(/-/g, '_');

  const mappedFilterName =
    urlFiltersToCioFacets[snakeCaseFacetName] || snakeCaseFacetName;
  const prefixAndSuffixRemoved = mappedFilterName
    .replace(/^tag_/, '')
    .replace(/_list$/, '');

  return getBrandValue({
    savage: slugify(mappedFilterName, '-'),
    default: slugify(prefixAndSuffixRemoved, '-'),
  });
};

/**
 * Generates a slugified version of the selected CIO facet and option display name.
 * This is used to create a unique RB identifier for the selected facet option.
 * Per FLD-2548, Savage brand does not process the prefix and suffix.
 * Pending product to align with syncing RBs and data autotags to use the same naming conventions
 * as all other brands.
 *
 * @param {string} facetName - The name of the facet.
 * @param {string} facetOptionDisplayName - The display name of the facet option.
 * @returns {string} - The slugified version of the selected CIO facet and option display name.
 */
export const getSelectedFacetRbid = (facetName, facetOptionDisplayName) => {
  // Ensure there was no previous slugification into kebab case for cio to url filters mapping
  const snakeCaseFacetName = facetName.replace(/-/g, '_');

  const mappedFacetName =
    cioFacetsToUrlFilters[snakeCaseFacetName] || snakeCaseFacetName;
  const prefixAndSuffixRemoved = mappedFacetName
    .replace(/^tag_/, '')
    .replace(/_list$/, '');

  return getBrandValue({
    savage: slugify(`${mappedFacetName}_${facetOptionDisplayName}`, '_'),
    default: slugify(
      `${prefixAndSuffixRemoved}_${facetOptionDisplayName}`,
      '_'
    ),
  });
};

/**
 * Returns the translated display name for a facet option.
 *
 * @param {string} facetName - The name of the facet.
 * @param {Object} facetOption - The facet option object containing `display_name` and `value`.
 * @param {string} domain - The domain to use for translation.
 * @returns {string} - The translated display name for the facet option.
 */
export const getTranslatedFacetOptionDisplayName = ({
  facetName,
  facetOption,
  domain,
}) => {
  const categoryName = cioSizeFacetsToFilterPreferences[facetName];
  let translatedDisplayName = facetOption?.display_name;
  if (categoryName && facetOption?.display_name === facetOption?.value) {
    translatedDisplayName = sizeByKey({
      domain,
      gender: 'F',
      category: categoryName,
      key: facetOption.value,
    });
  }

  const displayName = getBrandValue({
    savage: translatedDisplayName || facetOption?.display_name,
    default: facetOption?.display_name,
  });

  return displayName;
};

/**
 * Returns the initial selected band size value based on the URL filters.
 * If no band size is selected in the URL filters, it returns the first available band size.
 *
 * @param {Object} selectedUrlFilters - The current URL filters.
 * @param {Array} availableBandSizes - The list of available band sizes.
 * @param {string} facetName - The name of the facet for band size.
 * @returns {string} - The initial selected band size value.
 */
export const getInitialSelectedBandSizeValue = ({
  selectedUrlFilters,
  availableBandSizes,
  facetName,
}) => {
  const urlFilterName = cioFacetsToUrlFilters[facetName] || facetName;
  const firstSelectedUrlFilter = selectedUrlFilters?.[urlFilterName]?.[0];
  const parsedBraSizes = parseBraSizes({
    braSizes: firstSelectedUrlFilter ? [firstSelectedUrlFilter] : [],
  });
  const firstSelectedBandSize = Object.keys(parsedBraSizes)?.[0];

  return firstSelectedBandSize || availableBandSizes?.[0]?.value;
};

/**
 * Returns the facets to render based on the grid type.
 * For CONSTRUCTOR_SEARCH_GRID, it filters and sorts the facets to ensure size facets come first
 * and only includes facets with sub-facets, selected options, or more than one option.
 * It also limits the number of facets to MAX_FACET_COUNT.
 * For other grid types, it returns the facets unmodified.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.gridType - The type of the grid.
 * @param {Array} params.facets - The list of facets to filter and sort.
 * @returns {Array} - The filtered and sorted list of facets for the specified grid type
 */
export const getFacetsToRenderByGridType = ({ gridType, facets = [] }) => {
  if (gridType === GRID_TYPES.CONSTRUCTOR_SEARCH_GRID) {
    const sortedFacets = facets?.toSorted((a, b) => {
      if (a?.name === 'size' && b?.name !== 'size') {
        return -1; // Size facets should always come first
      }
      return 0; // Maintain original order for other facets
    });

    const filteredFacets = sortedFacets?.filter((facet) => {
      const isGroupedSizeFacet =
        facet?.name === 'size' && facet?.sub_facets?.length > 0;
      const hasAtLeastOneSelectedOption = facet?.options?.some(
        (option) => option.status === 'selected'
      );
      const hasMoreThanOneOption = facet?.options?.length > 1;

      return (
        isGroupedSizeFacet ||
        hasAtLeastOneSelectedOption ||
        hasMoreThanOneOption
      );
    });

    const facetsWithSelectedOptions = filteredFacets?.filter(
      (facet) =>
        facet?.options?.some((option) => option.status === 'selected') ||
        (facet?.name === 'size' && facet?.sub_facets?.length > 0)
    );

    return facetsWithSelectedOptions?.length > MAX_FACET_COUNT
      ? filteredFacets.slice(0, facetsWithSelectedOptions.length)
      : filteredFacets.slice(0, MAX_FACET_COUNT);
  }

  // By default, return facets unmodified
  return facets;
};

/**
 * Combines multiple custom facet filter objects into a single merged and deduplicated filter object.
 * Each custom facet filter can have a `filters` property containing key-value pairs where values are arrays.
 * This function merges all filter values for the same key across all input objects and removes duplicates.
 *
 * @param {Object} customFacetFilters - An object containing multiple custom facet filter objects.
 * Each value should have a `filters` property with filter key-value pairs.
 * @returns {Object} A combined filter object with merged and deduplicated array values for each filter key.
 *
 * @example
 * const customFilters = {
 *   filter1: { filters: { color: ['red', 'blue'], size: ['M'] } },
 *   filter2: { filters: { color: ['blue', 'green'], category: ['tops'] } },
 * };
 * combineCustomFacetFilters(customFilters);
 * // Returns: { color: ['red', 'blue', 'green'], size: ['M'], category: ['tops'] }
 */
export const combineCustomFacetFilters = (customFacetFilters) => {
  const combined = {};

  Object.values(customFacetFilters).forEach((item) => {
    if (item.hasError) {
      return;
    }

    const filters = item.filters || {};

    for (const key in filters) {
      const value = filters[key];

      if (!combined[key]) {
        combined[key] = [];
      }

      // merge + dedupe
      combined[key] = Array.from(new Set([...combined[key], ...value]));
    }
  });

  return combined;
};

/**
 * Checks if the given facet name and option value should use image swatches.
 * This enables CDN-based filter display for textures/patterns.
 * Supports both full facet matching and specific value matching.
 *
 * Config format:
 * {
 *   "denim_wash": true,              // All values for 'denim_wash' facet use images
 *   "tag_color": ["Multi", "Print"], // Only these values for 'tag_color' facet use images
 *   "list": true                     // All values for 'list' facet use images
 * }
 *
 * Note: Keys must be actual facet field names (e.g., 'tag_color', 'list', 'color', 'denim_wash').
 * Values can be:
 * - `true`: All option values for this facet will use image swatches
 * - `string[]`: Only the specified option values for this facet will use image swatches
 *
 * @param {Object} params - The parameters object
 * @param {string} params.facetName - The name of the facet to check (e.g., 'tag_color', 'list').
 * @param {string|null} [params.optionValue=null] - The specific option value to check (e.g., 'Multi', 'Print').
 * @param {Object|null} [params.imageSwatchFilters=null] - Config object mapping facet field names to true (all values) or array of specific values.
 * @return {boolean} - Returns true if the facet/option should use image swatches.
 */
export const isImageSwatchFacet = ({
  facetName,
  optionValue = null,
  imageSwatchFilters = null,
}) => {
  if (!imageSwatchFilters || !facetName) {
    return false;
  }

  // Check if facet exists in config
  const facetConfig = imageSwatchFilters[facetName];

  if (!facetConfig) {
    return false;
  }

  // If facetConfig is true, match all values
  if (facetConfig === true) {
    return true;
  }

  // If facetConfig is an array, check if optionValue is in the array
  if (Array.isArray(facetConfig) && optionValue !== null) {
    return facetConfig.includes(optionValue);
  }

  return false;
};

/**
 * Generates a CDN URL for a filter image swatch.
 * Example: ("Denim Wash", "Light Wash", "https://cdn.fabletics.com/media/images")
 *          -> "https://cdn.fabletics.com/media/images/filters/denim_wash/light_wash.jpg"
 *
 * @param {string} filterName - The filter name (e.g., "Denim Wash")
 * @param {string} optionValue - The option value (e.g., "Light Wash")
 * @param {string} cdnBaseUrl - The CDN base URL (from config)
 * @return {string} The generated CDN URL
 */
export const generateFilterImageUrl = (filterName, optionValue, cdnBaseUrl) => {
  const sanitizedFilter = slugify(filterName, '_');
  const sanitizedOption = slugify(optionValue, '_');
  return `${cdnBaseUrl}/filters/${sanitizedFilter}/${sanitizedOption}.jpg`;
};
